import axios from "axios";

let defaultBase = "http://localhost:8000";
if (typeof window !== "undefined") {
  const h = window.location.hostname;
  if (h === "localhost" || h === "127.0.0.1") {
    defaultBase = "http://localhost:8000";
  } else if (/^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.test(h)) {
    // Phone or tablet accessing via Wi-Fi IP
    defaultBase = `http://${h}:8000`;
  } else {
    // Production domain (e.g. register.cgcollective.in)
    defaultBase = "https://cgc-backend-kmjn.onrender.com";
  }
}
const isLocal = typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
const isLocalIP = typeof window !== "undefined" && /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.test(window.location.hostname);

const rawBase = isLocal
  ? "http://localhost:8000"
  : (isLocalIP
      ? `http://${window.location.hostname}:8000`
      : (process.env.REACT_APP_BACKEND_URL || defaultBase));

export const API = rawBase ? `${rawBase.replace(/\/$/, "")}/api` : "/api";

export const api = axios.create({ baseURL: API });

// Latency & Cold-Start detector
let pendingCount = 0;
let latencyTimer = null;

function notifyServerStatus(isWarming) {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("cgc:server-warming", { detail: { warming: isWarming } }));
  }
}

api.interceptors.request.use((cfg) => {
  pendingCount++;
  // If this is the first pending request and it takes longer than 2.2s, notify that server is likely waking up
  if (pendingCount === 1 && !latencyTimer) {
    latencyTimer = setTimeout(() => {
      if (pendingCount > 0) {
        notifyServerStatus(true);
      }
    }, 2200);
  }

  // Never override an Authorization header the caller explicitly set (e.g. portal tokens)
  const explicit = cfg.headers?.Authorization || cfg.headers?.authorization;
  if (explicit) return cfg;
  // Don't attach the staff token to portal endpoints — portal has its own token
  const url = (cfg.url || "").toString();
  if (url.startsWith("/portal/")) return cfg;
  const t = localStorage.getItem("cgc_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

api.interceptors.response.use(
  (res) => {
    pendingCount = Math.max(0, pendingCount - 1);
    if (pendingCount === 0) {
      if (latencyTimer) {
        clearTimeout(latencyTimer);
        latencyTimer = null;
      }
      notifyServerStatus(false);
    }
    return res;
  },
  (err) => {
    pendingCount = Math.max(0, pendingCount - 1);
    if (pendingCount === 0) {
      if (latencyTimer) {
        clearTimeout(latencyTimer);
        latencyTimer = null;
      }
      notifyServerStatus(false);
    }
    return Promise.reject(err);
  }
);

export async function pingServerHealth() {
  try {
    const res = await axios.get(`${API}/health`, { timeout: 3500 });
    return res.status === 200;
  } catch {
    return false;
  }
}

export const fmtINR = (paise) => {
  const p = paise || 0;
  const hasPaise = p % 100 !== 0;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: hasPaise ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(p / 100);
};

export const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

export const fmtDateShort = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
};
